import React from 'react';
import styled, { keyframes } from 'styled-components';
import ScrollFadeIn from '../components/ScrollFadeIn';
import { Mic, FileText, Brain, Server, Check } from 'lucide-react';

const AristoPage: React.FC = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroIcon><Mic /></HeroIcon>
          <HeroTitle>ARISTO</HeroTitle>
          <HeroTagline>AI-powered Real-time Interactive Speaking Tutor</HeroTagline>
          <HeroDescription>
            학생이 업로드한 PDF 교재를 기반으로 AI가 실시간 소크라테스식 질문을 통해
            개념을 깊이 있게 이해하도록 유도하는 음성 튜터링 플랫폼입니다.
          </HeroDescription>
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <SectionTitle>주요 기능</SectionTitle>
        <FeaturesGrid>
          <ScrollFadeIn direction="up" delay={0.1}>
            <FeatureCard>
              <FeatureIcon><Brain /></FeatureIcon>
              <FeatureTitle>실시간 소크라테스식 튜터링</FeatureTitle>
              <FeatureDescription>
                정답을 바로 알려주는 대신, 학생 스스로 깨달음을 얻을 수 있도록 끝없는 꼬리 질문(Follow-up)을 던지는 AI 튜터입니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>
          <ScrollFadeIn direction="up" delay={0.2}>
            <FeatureCard>
              <FeatureIcon><FileText /></FeatureIcon>
              <FeatureTitle>PDF 출처 기반 맞춤형 학습</FeatureTitle>
              <FeatureDescription>
                PDF 파일을 업로드하면 AI가 자료를 학습하여, 학생의 대답에 대해 정확한 교재 출처 기반으로 피드백을 제공합니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>
          <ScrollFadeIn direction="up" delay={0.3}>
            <FeatureCard>
              <FeatureIcon><Server /></FeatureIcon>
              <FeatureTitle>안정적인 확장형 아키텍처</FeatureTitle>
              <FeatureDescription>
                프론트엔드, 세션 관리 서버, AI 처리 서버가 분리된 확장 가능한 구조로 안정적인 서비스를 제공합니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>
        </FeaturesGrid>
      </FeaturesSection>

      <BenefitsSection>
        <BenefitsContent>
          <BenefitsText>
            <SectionTitle>아키텍처 및 구현 기술</SectionTitle>
            <BenefitsList>
              <ScrollFadeIn direction="up" delay={0.1}>
                <BenefitItem>
                  <BenefitIcon><Check /></BenefitIcon>
                  <BenefitText>
                    <BenefitTitle>Realtime Voice AI</BenefitTitle>
                    <BenefitDescription>
                      저지연 실시간 음성 통신으로 실제 외국어 튜터와 대화하는 듯한 매끄러운 경험을 제공합니다.
                    </BenefitDescription>
                  </BenefitText>
                </BenefitItem>
              </ScrollFadeIn>
              <ScrollFadeIn direction="up" delay={0.2}>
                <BenefitItem>
                  <BenefitIcon><Check /></BenefitIcon>
                  <BenefitText>
                    <BenefitTitle>출처 기반 지식 검색</BenefitTitle>
                    <BenefitDescription>
                      방대한 분량의 PDF 안에서도 학생 질문에 가장 부합하는 문단을 즉시 찾아 정확한 근거를 제시합니다.
                    </BenefitDescription>
                  </BenefitText>
                </BenefitItem>
              </ScrollFadeIn>
              <ScrollFadeIn direction="up" delay={0.3}>
                <BenefitItem>
                  <BenefitIcon><Check /></BenefitIcon>
                  <BenefitText>
                    <BenefitTitle>클라우드 기반 세션 동기화</BenefitTitle>
                    <BenefitDescription>
                      클라우드를 통해 사용자의 대화 내역과 학습 상태 기록이 실시간으로 안전하게 동기화됩니다.
                    </BenefitDescription>
                  </BenefitText>
                </BenefitItem>
              </ScrollFadeIn>
            </BenefitsList>
          </BenefitsText>
        </BenefitsContent>
      </BenefitsSection>

      <CTASection>
        <CTAContent>
          <CTATitle>ARISTO 소스 코드 살펴보기</CTATitle>
          <CTADescription>
            ARISTO의 혁신적인 기술력을 지금 바로 확인해보세요.
          </CTADescription>
          <CTAButtons>
            <PrimaryButton href="https://youtu.be/zuRYcA1nuX4" target="_blank" rel="noopener noreferrer">
              시연 영상 보기
            </PrimaryButton>
          </CTAButtons>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
};

export default AristoPage;

/* ────────── Styled Components (SocraPage 기반 통일) ────────── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const PageContainer = styled.div`
  padding-top: 80px;
`;
const HeroSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeIn} 1s ease-out;
`;
const HeroIcon = styled.div`
  font-size: 3.25rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.accent};
`;
const HeroTitle = styled.h1`
  font-size: 2.6rem;
  font-weight: 800;
  margin-bottom: 1rem;
`;
const HeroTagline = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 2rem;
  opacity: 0.95;
`;
const HeroDescription = styled.p`
  font-size: 1.25rem;
  line-height: 1.8;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 2rem;
`;

/* ── Features ── */
const FeaturesSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;
const FeaturesGrid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(350px, 100%), 1fr));
  gap: 2rem;
`;
const FeatureCard = styled.div`
  padding: 1.75rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  backdrop-filter: blur(12px);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: ${({ theme }) => theme.transitions.cubic};
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;
const FeatureIcon = styled.div`
  font-size: 2.1rem;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 1.5rem;
`;
const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;
const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ── Benefits ── */
const BenefitsSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;
const BenefitsContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;
const BenefitsText = styled.div``;
const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
`;
const BenefitItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;
const BenefitIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0.25rem;
`;
const BenefitText = styled.div`
  flex: 1;
`;
const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;
const BenefitDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ── CTA ── */
const CTASection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;
const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const CTATitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
`;
const CTADescription = styled.p`
  font-size: 1.5rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`;
const CTAButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
`;
const PrimaryButton = styled.a`
  display: inline-block;
  padding: 0.9rem 2rem;
  background: #ffffff;
  color: #020617;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1.2rem;
  border: none;
  transition: ${({ theme }) => theme.transitions.cubic};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  }
`;
